import React, { FC, useRef, useState } from 'react';

type VideoBannerProps = {
  videoUrl: string;
  poster?: string;
  height?: string | number;
  width?: string | number;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  overlayText?: string;
  fallbackText?: string;
};

// Function to convert YouTube URL to embed format
const convertToEmbedUrl = (url: string): string => {
  if (!url) return '';

  // Handle YouTube URLs
  const youtubeRegex = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(youtubeRegex);

  if (match && match[2].length === 11) {
    const videoId = match[2];
    // Extract start time if present
    const startTimeMatch = url.match(/[?&]t=(\d+)/);
    const startTime = startTimeMatch ? startTimeMatch[1] : '0';
    return `https://www.youtube.com/embed/${videoId}?start=${startTime}&autoplay=1&mute=1&loop=1&playlist=${videoId}`;
  }

  return url;
};

// Check if URL is a YouTube URL
const isYouTubeUrl = (url: string): boolean => {
  return url.includes('youtube.com') || url.includes('youtu.be');
};

const VideoBanner: FC<VideoBannerProps> = ({
  videoUrl,
  poster,
  height = '400px',
  width = '100%',
  autoPlay = true,
  muted = true,
  loop = true,
  overlayText,
  fallbackText = 'Video not available',
}) => {
  console.log(videoUrl);
  const [hasError, setHasError] = useState(false);

  const isYouTube = isYouTubeUrl(videoUrl);
  const embedUrl = isYouTube ? convertToEmbedUrl(videoUrl) : videoUrl;

  const handleVideoError = () => {
    setHasError(true);
  };

  if (!videoUrl || hasError) {
    return (
      <div
        style={{
          position: 'relative',
          width: typeof width === 'number' ? `${width}px` : width,
          height: typeof height === 'number' ? `${height}px` : height,
          backgroundColor: 'lightgrey',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          color: '#666',
        }}>
        {fallbackText}
      </div>
    );
  }

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    width: typeof width === 'number' ? `${width}px` : width,
    height: typeof height === 'number' ? `${height}px` : height,
    overflow: 'hidden',
    cursor: 'pointer',
  };

  const overlayStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#fff',
    background: 'rgba(0, 0, 0, 0.3)',
    pointerEvents: 'none',
  };

  return (
    <div style={containerStyle}>
      {isYouTube ? (
        <iframe
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          src={embedUrl}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onError={handleVideoError}
        />
      ) : (
        <video
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          src={embedUrl}
          poster={poster}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline
          onError={handleVideoError}
          controls
        />
      )}
      {overlayText && <div style={overlayStyle}>{overlayText}</div>}
    </div>
  );
};

export default VideoBanner;
